/**
 * public/audio/manifest.json (DOM-free parsing + resolution).
 *
 *   { "sounds": { "block.stone.break": ["block/stone/break1.ogg", ...], ... },
 *     "music":  { "game": ["music/game/calm1.ogg"], "menu": [...], ... } }
 *
 * Paths are relative to /audio/. An id with no files (or a missing manifest)
 * uses the procedural placeholder from the registry.
 */
export interface Manifest {
  sounds: Record<string, string[]>;
  music: Record<string, string[]>;
  /** Per-file [volume, pitch] multipliers (vanilla sounds.json entries), keyed by file path. */
  tweaks: Record<string, [number, number]>;
}

export const EMPTY_MANIFEST: Manifest = { sounds: {}, music: {}, tweaks: {} };

export function parseManifest(raw: unknown): Manifest {
  const out: Manifest = { sounds: {}, music: {}, tweaks: {} };
  if (!raw || typeof raw !== 'object') return out;
  const r = raw as Record<string, unknown>;
  if (r.tweaks && typeof r.tweaks === 'object') {
    for (const [f, t] of Object.entries(r.tweaks as Record<string, unknown>)) {
      if (Array.isArray(t) && typeof t[0] === 'number' && typeof t[1] === 'number') out.tweaks[f] = [t[0], t[1]];
    }
  }
  for (const key of ['sounds', 'music'] as const) {
    const sec = r[key];
    if (!sec || typeof sec !== 'object') continue;
    for (const [id, list] of Object.entries(sec as Record<string, unknown>)) {
      const files = Array.isArray(list) ? list.filter((f): f is string => typeof f === 'string' && f.length > 0) : [];
      if (files.length) out[key][id] = files;
    }
  }
  return out;
}

/** `over` on top of `base`: an id listed in both uses over's files. */
export function mergeManifest(base: Manifest, over: Manifest): Manifest {
  return {
    sounds: { ...base.sounds, ...over.sounds },
    music: { ...base.music, ...over.music },
    tweaks: { ...base.tweaks, ...over.tweaks },
  };
}

/** Vanilla reuses step samples for hit / fall; other ids have no fallback. */
export function fallbackIds(id: string): string[] {
  const m = /^block\.(\w+)\.(hit|fall)$/.exec(id);
  return m ? [id, `block.${m[1]}.step`] : [id];
}

export type Resolved = { kind: 'files'; id: string; files: string[] } | { kind: 'synth'; id: string };

export function resolveSound(man: Manifest, id: string): Resolved {
  for (const cand of fallbackIds(id)) {
    const f = man.sounds[cand];
    if (f && f.length) return { kind: 'files', id: cand, files: f };
  }
  return { kind: 'synth', id };
}

export function musicFiles(man: Manifest, mood: string): string[] {
  return man.music[mood] ?? [];
}

/** Random index in [0, n) that avoids `last` when there is a choice. */
export function pickVariant(n: number, last: number, rnd: () => number): number {
  if (n <= 1) return 0;
  let i = Math.floor(rnd() * (n - 1));
  if (i >= last && last >= 0) i++;
  return Math.min(n - 1, i);
}
